import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { storage } from "../firebase";
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import { useUser } from "./UserContext";

const AdminCourseImagesContext = createContext(null);
export const useAdminCourseImages = () => useContext(AdminCourseImagesContext);

/* ===== API base ===== */
const ENV_BASE =
  (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  "";
const CLEAN_ENV_BASE = (ENV_BASE || "").replace(/\/+$/, "");
const FALLBACK_BASE = "https://www.doroosonline.co.il";
const API_BASE = CLEAN_ENV_BASE || FALLBACK_BASE;
const url = (p) => `${API_BASE}${p.startsWith("/") ? p : `/${p}`}`;

const getAuthHeaders = () => {
  const token =
    (typeof window !== "undefined" && (window.__userToken || "")) ||
    localStorage.getItem("token") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("authToken") ||
    "";

  const headers = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

async function parseJSON(res) {
  const text = await res.text().catch(() => "");
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {}
  return { text, data };
}

export const AdminCourseImagesProvider = ({ children }) => {
  const { showAlert, role, userType } = useUser();
  const isAdmin =
    String(role || "").toLowerCase() === "admin" ||
    String(userType || "").toLowerCase() === "admin";

  // --- State ---
  const [categories, setCategories] = useState([]); // [{id,slug,name}]
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [images, setImages] = useState([]); // [{id,url,alt_text,sort_order,is_active}]
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingImages, setLoadingImages] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef(null);

  // --- Config ---
  const maxBytes = 5 * 1024 * 1024;
  const allowedFormats = ["jpg", "jpeg", "png", "bmp", "gif", "webp"];

  const isValidImage = (file) => {
    const ext = (file?.name || "").split(".").pop().toLowerCase();
    return allowedFormats.includes(ext);
  };

  // --- API calls (admin) ---
  const api = useMemo(() => {
    return {
      listCategories: async () => {
        const res = await fetch(url("/api/admin/course-image-categories"), {
          method: "GET",
          headers: { ...getAuthHeaders() },
        });
        const { data, text } = await parseJSON(res);
        if (!res.ok) throw new Error(data?.message || text || "Failed to load categories");
        return data?.categories || [];
      },

      listImagesByCategory: async (categoryId) => {
        const res = await fetch(
          url(`/api/admin/course-image-categories/${encodeURIComponent(categoryId)}/images`),
          {
            method: "GET",
            headers: { ...getAuthHeaders() },
          }
        );
        const { data, text } = await parseJSON(res);
        if (!res.ok) throw new Error(data?.message || text || "Failed to load images");
        // expect: { images: [...] }
        return data?.images || [];
      },

      addCourseImageRecord: async ({ category_id, url: imageUrl, alt_text, sort_order }) => {
        const res = await fetch(
          url(`/api/admin/course-image-categories/${encodeURIComponent(category_id)}/images`),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...getAuthHeaders(),
            },
            body: JSON.stringify({
              url: imageUrl,
              alt_text: alt_text || "",
              sort_order: Number.isFinite(Number(sort_order)) ? Number(sort_order) : 0,
              is_active: 1,
            }),
          }
        );
        const { data, text } = await parseJSON(res);
        if (!res.ok) throw new Error(data?.message || text || "Failed to save image record");
        // you can return {image:{...}} or just ok
        return data?.image || null;
      },

      deleteCourseImage: async (imageId) => {
        const res = await fetch(url(`/api/admin/course-images/${encodeURIComponent(imageId)}`), {
          method: "DELETE",
          headers: { ...getAuthHeaders() },
        });
        const { data, text } = await parseJSON(res);
        if (!res.ok) throw new Error(data?.message || text || "Failed to delete image");
        return true;
      },
    };
  }, []);

  // --- Load categories on mount ---
  useEffect(() => {
    if (!isAdmin) return;

    let mounted = true;
    (async () => {
      setLoadingCategories(true);
      try {
        const cats = await api.listCategories();
        if (!mounted) return;
        setCategories(cats);

        // auto-select first category
        if (!selectedCategoryId && cats?.[0]?.id) {
          setSelectedCategoryId(String(cats[0].id));
        }
      } catch (e) {
        console.error("[AdminCourseImages] load categories error:", e);
        showAlert?.(e?.message || "שגיאה בטעינת קטגוריות", "warning");
      } finally {
        if (mounted) setLoadingCategories(false);
      }
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // --- Load images when category changes ---
  const refreshImages = useCallback(async () => {
    if (!isAdmin || !selectedCategoryId) {
      setImages([]);
      return;
    }
    setLoadingImages(true);
    try {
      const rows = await api.listImagesByCategory(selectedCategoryId);
      // usually you want only active on backend; but keep safe:
      const activeOnly = (rows || []).filter((r) => Number(r?.is_active ?? 1) === 1);
      setImages(activeOnly);
    } catch (e) {
      console.error("[AdminCourseImages] load images error:", e);
      showAlert?.(e?.message || "שגיאה בטעינת תמונות", "warning");
    } finally {
      setLoadingImages(false);
    }
  }, [api, isAdmin, selectedCategoryId, showAlert]);

  useEffect(() => {
    refreshImages();
  }, [refreshImages]);

  // --- Upload flow: file -> Firebase -> DB record -> UI ---
  const addImage = async (file, { altText = "", sortOrder = 0 } = {}) => {
    if (!selectedCategoryId) {
      showAlert?.("בחר קטגוריה קודם", "warning");
      return;
    }
    if (!file) return;

    if (!isValidImage(file)) {
      showAlert?.("פורמט לא חוקי. ניתן להעלות JPG/PNG/WEBP...", "warning");
      return;
    }
    if (file.size > maxBytes) {
      showAlert?.("הקובץ גדול מ־5MB", "warning");
      return;
    }

    setUploading(true);

    // store in: course_images/<categoryId>/<uuid>.<ext>
    const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
    const storageName = `${uuidv4()}.${ext}`;
    const blobRef = storageRef(storage, `course_images/${selectedCategoryId}/${storageName}`);

    try {
      // 1) upload to Firebase
      await uploadBytes(blobRef, file);

      // 2) get url
      const imageUrl = await getDownloadURL(blobRef);

      // 3) save DB record
      await api.addCourseImageRecord({
        category_id: selectedCategoryId,
        url: imageUrl,
        alt_text: altText,
        sort_order: sortOrder,
      });

      showAlert?.("התמונה נשמרה", "success");

      // 4) refresh list
      await refreshImages();
    } catch (e) {
      console.error("[AdminCourseImages] upload error:", e);
      showAlert?.(e?.message || "שגיאה בהעלאה", "warning");

      // cleanup storage if DB failed
      try {
        await deleteObject(blobRef);
      } catch {}
    } finally {
      setUploading(false);
    }
  };

  const addImages = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    // sequential is safer; you can parallel later
    for (const f of list) {
      // eslint-disable-next-line no-await-in-loop
      await addImage(f);
    }
  };

  // --- Delete flow: DB (soft) + optionally delete Firebase object ---
  // NOTE: since you store full URL in DB, deleting from Firebase requires parsing the path.
  // easiest: just soft-delete in DB (is_active=0) and keep file in storage.
  // If you want to physically delete later, we can add a "file_name" column.
  const deleteImage = async (imageRow) => {
    const imageId = imageRow?.id;
    if (!imageId) return;

    setLoadingImages(true);
    try {
      await api.deleteCourseImage(imageId);
      showAlert?.("התמונה נמחקה", "success");
      await refreshImages();
    } catch (e) {
      console.error("[AdminCourseImages] delete error:", e);
      showAlert?.(e?.message || "שגיאה במחיקה", "warning");
    } finally {
      setLoadingImages(false);
    }
  };

  // --- Input/drag handlers (same as your ProfilePhotoContext) ---
  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files?.length) addImages(files);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer?.files;
    if (files?.length) addImages(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const value = useMemo(
    () => ({
      // flags
      isAdmin,

      // state
      categories,
      selectedCategoryId,
      images,
      loadingCategories,
      loadingImages,
      uploading,
      isDragging,

      // refs
      fileInputRef,

      // setters
      setSelectedCategoryId,

      // actions
      refreshImages,
      handleUploadClick,
      handleFileChange,
      handleDrop,
      handleDragOver,
      handleDragLeave,
      deleteImage,
    }),
    [
      isAdmin,
      categories,
      selectedCategoryId,
      images,
      loadingCategories,
      loadingImages,
      uploading,
      isDragging,
      refreshImages,
    ]
  );

  return (
    <AdminCourseImagesContext.Provider value={value}>
      {children}
    </AdminCourseImagesContext.Provider>
  );
};